import {
  generateUUID,
  getCookie,
  setCookie,
} from '@kizi/comparator-components';
import moment from 'moment';

const getFieldValue = (fields, fieldName) => {
  return fields.find((e) => e.name.includes(fieldName))?.value || '';
};

const formatDate = (fieldValue) => {
  return moment(fieldValue, 'DD-MM-YYYY').startOf('day').format();
};

const calculateAges = (fields) => {
  const birthdateFields = [
    'childBirthdate1',
    'childBirthdate2',
    'childBirthdate3',
    'childBirthdate4',
    'childBirthdate5',
  ];

  return birthdateFields
    .map((field) => {
      return calculateAge(fields, field);
    })
    .filter((f) => f !== null);
};

const calculateAge = (fields, birthdateField) => {
  const birthdate = getFieldValue(fields, birthdateField);
  if (!birthdate) return null;

  const formattedBirthdate = moment(birthdate, 'DD-MM-YYYY');
  return moment().diff(formattedBirthdate, 'year');
};

const parseCoverages = (fields) => {
  const coverages = JSON.parse(getFieldValue(fields, 'summary'));
  let result = {};
  coverages.forEach((coverage) => {
    result[coverage.title] = coverage.selectedCoverageText;
  });
  return result;
};

const getSessionId = () => {
  const peregrineSessionId = getCookie('psi');
  if (!peregrineSessionId) {
    const session_id = generateUUID();
    setCookie('psi', session_id, 30);
    console.log('Peregrine session id expired. A new one will be generated.');
    return session_id;
  }
  return peregrineSessionId;
};

const getCommonAttributes = (session_id) => {
  return {
    session_id,
    'event_type': 'visit',
    'event_source': window.location.hostname,
    'uri': window.location.href,
    'user_agent': window.navigator.userAgent,
    'os': window.navigator.platform,
    'platform': window.navigator.platform,
    'timestamp': new Date().toISOString(),
  };
};

const getMainApplicantData = (fields, userHash): {} => {
  const email = getFieldValue(fields, 'optionalemail');
  const birthdate = getFieldValue(fields, 'birthdate');
  return {
    'mainapplicant': {
      'pc6': getFieldValue(fields, 'postcode'),
      'id': userHash,
      ...(email && { email }),
      ...(birthdate && {
        'birthday': formatDate(birthdate),
      }),
    },
  };
};

const getStep1Data = (fields, partnerHash) => {
  const partnerBirthdate = getFieldValue(fields, 'partnerBirthdate');

  return {
    'student': getFieldValue(fields, 'isStudent') === 'default',
    'gender': getFieldValue(fields, 'gender'),
    ...(partnerBirthdate && {
      'partner': {
        'id': partnerHash,
        'birthday_partner': formatDate(partnerBirthdate),
      },
    }),
    'num_of_children': JSON.parse(
      getFieldValue(fields, 'birthdateContextFields'),
    ).length,
    'age_of_insured_persons': calculateAges(fields),
  };
};

const getStep2Data = (fields) => {
  return {
    deductible: getFieldValue(fields, 'deductibleAmount'),
    coverages: parseCoverages(fields),
  };
};

const getStep3Data = (fields) => {
  const filters = JSON.parse(getFieldValue(fields, 'filters'));
  return {
    'filter_payment_terms':
      filters.showPriceMode[0] === 'month' ? 'Maand' : 'Jaar',
    'filter_rivm_type': filters.rivm,
    'filter_deductible': getFieldValue(fields, 'deductibleAmount'),
    'filter_choicefreedom': filters.freeChoiceOfCaregiver,
    'filter_waittime': filters.strictestLevelNewInsured,
    'filter_supplier': filters.providerName,
  };
};

export const getSetupEvent = () => {
  const session_id = getSessionId();

  return {
    ...getCommonAttributes(session_id),
  };
};

export const getWidgetDataEvent = (fields, userHash) => {
  const session_id = getSessionId();

  return {
    ...getCommonAttributes(session_id),
    ...getMainApplicantData(fields, userHash),
  };
};

export const getStep1DataEvent = (
  fields,
  userHash,
  partnerHash,
  activeExperiments,
) => {
  const session_id = getSessionId();

  return {
    'funnel_step': '1',
    'active_experiments': activeExperiments,
    ...getCommonAttributes(session_id),
    ...getMainApplicantData(fields, userHash),
    ...{
      health: {
        ...getStep1Data(fields, partnerHash),
      },
    },
  };
};

export const getStep2DataEvent = (
  fields,
  userHash,
  partnerHash,
  activeExperiments,
) => {
  const session_id = getSessionId();

  return {
    'funnel_step': '2',
    'active_experiments': activeExperiments,
    ...getCommonAttributes(session_id),
    ...getMainApplicantData(fields, userHash),
    ...{
      health: {
        ...getStep1Data(fields, partnerHash),
        ...getStep2Data(fields),
      },
    },
  };
};

export const getStep3DataEvent = (
  fields,
  userHash,
  partnerHash,
  activeExperiments,
) => {
  const session_id = getSessionId();

  return {
    'funnel_step': '3',
    'active_experiments': activeExperiments,
    'comparison_id': getFieldValue(fields, 'requestLogId'),
    ...getCommonAttributes(session_id),
    ...getMainApplicantData(fields, userHash),
    ...{
      health: {
        ...getStep1Data(fields, partnerHash),
        ...getStep2Data(fields),
        ...getStep3Data(fields),
      },
    },
  };
};

export const getCheckoutEvent = (
  fields,
  userHash,
  partnerHash,
  activeExperiments,
  conversionType = 'purchase',
) => {
  const session_id = getSessionId();
  const product = JSON.parse(getFieldValue(fields, 'selectedProduct'));

  return {
    'funnel_step': '3',
    'conversion': true,
    'conversion_type': conversionType,
    'active_experiments': activeExperiments,
    'conversion_price_year': [product.analyticsData.price],
    'conversion_product_id': [product.analyticsData.item_id],
    'conversion_product_position': [product.analyticsData.index],
    'comparison_id': getFieldValue(fields, 'requestLogId'),
    ...getCommonAttributes(session_id),
    ...getMainApplicantData(fields, userHash),
    ...{
      health: {
        ...getStep1Data(fields, partnerHash),
        ...getStep2Data(fields),
        ...getStep3Data(fields),
      },
    },
  };
};
